import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { Badge, Button, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { supportMessageModel } from 'entities/support-message';
import { userLib, userModel } from 'entities/user';

import { routesApp } from 'shared/config';

import { NAV_DRAWER_ID, NavDrawer } from './nav-drawer';
import { NAV_ITEMS, NavItemWithActive, WIDE_NAV_MIN_WIDTH, getNavAriaLabel, withActive } from './nav-items';

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { data: user } = userModel.useUser();
  // Hook factories from the model layer: must stay unconditional at the top of Header.
  supportMessageModel.getNewMessagesCountAsync()(dispatch);
  const newMessagesCount = supportMessageModel.useNewMessagesCount();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isWide = useMediaQuery(theme.breakpoints.up(WIDE_NAV_MIN_WIDTH));
  const identityInToolbar = useMediaQuery(theme.breakpoints.up('sm'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const items = useMemo(() => withActive(NAV_ITEMS, location.pathname), [location.pathname]);

  const handleLogout = () => {
    window.localStorage.removeItem('token');
    navigate(routesApp.sendEmail);
  };

  const roleText = user?.role ? userLib.getUserRoleText(user.role) : '...';
  const emailText = user?.email ?? '...';

  return (
    <AppBar
      position="static"
      sx={{ bgcolor: 'common.white', color: 'text.primary', boxShadow: '0 2px 15px 0 rgba(0, 0, 0, .05)' }}
    >
      <Toolbar sx={{ gap: 1, minWidth: 0 }}>
        {isMobile && (
          <IconButton
            edge="start"
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls={drawerOpen ? NAV_DRAWER_ID : undefined}
            onClick={() => setDrawerOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography variant="h5" component="div" sx={{ flex: '0 0 auto', color: 'primary.main', mr: 1 }}>
          VaccineGo
        </Typography>

        <Box
          component={isMobile ? 'div' : 'nav'}
          aria-label={isMobile ? undefined : 'Primary navigation'}
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
              {items.map((item) =>
                isWide ? (
                  <WideNavButton key={item.path} item={item} newMessagesCount={newMessagesCount} />
                ) : (
                  <CompactNavButton key={item.path} item={item} newMessagesCount={newMessagesCount} />
                ),
              )}
            </Box>
          )}
        </Box>

        {identityInToolbar && (
          <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: isWide ? 180 : 160 }}>
            <Typography variant="body2" noWrap sx={{ color: 'primary.main' }}>
              {roleText}
            </Typography>
            <Typography variant="body2" noWrap title={emailText}>
              {emailText}
            </Typography>
          </Box>
        )}

        <IconButton aria-label="Log out" size="large" color="inherit" onClick={handleLogout} sx={{ flexShrink: 0 }}>
          <LogoutIcon fontSize="inherit" />
        </IconButton>
      </Toolbar>

      {isMobile && (
        <NavDrawer
          open={drawerOpen}
          onClose={closeDrawer}
          items={items}
          newMessagesCount={newMessagesCount}
          identity={identityInToolbar ? undefined : { role: roleText, email: emailText }}
          onLogout={identityInToolbar ? undefined : handleLogout}
        />
      )}
    </AppBar>
  );
};

type NavButtonProps = { item: NavItemWithActive; newMessagesCount: number };

const NavIcon = ({ item, newMessagesCount }: NavButtonProps) => {
  const { icon: Icon, path } = item;
  const showBadge = path === routesApp.supportMessages && newMessagesCount > 0;
  return showBadge ? (
    <Badge badgeContent={newMessagesCount} color="error" max={99}>
      <Icon />
    </Badge>
  ) : (
    <Icon />
  );
};

const activeUnderline = {
  content: '""',
  position: 'absolute',
  left: 8,
  right: 8,
  bottom: 0,
  height: 3,
  borderRadius: '3px 3px 0 0',
  bgcolor: 'primary.main',
} as const;

const WideNavButton = ({ item, newMessagesCount }: NavButtonProps) => (
  <Button
    component={RouterLink}
    to={item.path}
    aria-current={item.isActive ? 'page' : undefined}
    aria-label={getNavAriaLabel(item, newMessagesCount)}
    startIcon={<NavIcon item={item} newMessagesCount={newMessagesCount} />}
    sx={{
      position: 'relative',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      px: 1,
      minHeight: 44,
      color: item.isActive ? 'primary.main' : 'inherit',
      '&::after': item.isActive ? activeUnderline : undefined,
    }}
  >
    {item.label}
  </Button>
);

const CompactNavButton = ({ item, newMessagesCount }: NavButtonProps) => (
  <Tooltip title={item.label} enterDelay={300}>
    <IconButton
      component={RouterLink}
      to={item.path}
      aria-current={item.isActive ? 'page' : undefined}
      aria-label={getNavAriaLabel(item, newMessagesCount)}
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: 1,
        color: item.isActive ? 'primary.main' : 'inherit',
        '&::after': item.isActive ? activeUnderline : undefined,
      }}
    >
      <NavIcon item={item} newMessagesCount={newMessagesCount} />
    </IconButton>
  </Tooltip>
);
